import React, { useState } from 'react'
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField,
} from '@material-ui/core'

import { ERRORS } from 'consts'
import { snackbar } from 'components'
import { useApi, useApiCall } from 'hooks'
import CustomTextField from 'components/CustomTextField'

import {
  createReplyTemplate,
  deleteReplyTemplate,
  getReplyTemplates,
  updateReplyTemplate,
} from '../../replyTemplatesApi'
import {
  CreateReplyTemplatePayload,
  CreateReplyTemplateResponse,
  GetReplyTemplatesResponse,
  ReviewReplyTemplate,
  UpdateReplyTemplatePayload,
  UpdateReplyTemplateResponse,
} from '../../replyTemplateTypes'
import { DefaultTable } from '../../../components'

interface TemplateFormValues {
  title: string
  body: string
  isActive: boolean
}

const emptyForm: TemplateFormValues = {
  title: '',
  body: '',
  isActive: true,
}

const truncateBody = (body: string, maxLength = 80): string => {
  const trimmed = body.trim()
  if (trimmed.length <= maxLength) return trimmed
  return `${trimmed.slice(0, maxLength)}…`
}

const columns = [
  { title: 'ID', field: 'id' },
  { title: 'Título', field: 'title' },
  {
    title: 'Cuerpo',
    sorting: false,
    render: (rowData: ReviewReplyTemplate) => truncateBody(rowData.body),
  },
  {
    title: 'Activa',
    field: 'isActive',
    render: (rowData: ReviewReplyTemplate) => (rowData.isActive ? 'Sí' : 'No'),
  },
]

const getReplyTemplatesResponseGetter = (responseData: GetReplyTemplatesResponse) =>
  responseData?.replyTemplates ?? []

const getAutomaticReplyTemplates = () => getReplyTemplates(true)

const AutoReplyTemplatesSettings: React.FC = () => {
  const [{ data: templates, isLoading }, , setTemplates] = useApi<
    GetReplyTemplatesResponse,
    ReviewReplyTemplate[]
  >(getAutomaticReplyTemplates, getReplyTemplatesResponseGetter, {
    baseData: [],
    onError: () => snackbar.show(ERRORS.GENERIC_ERROR_MESSAGE),
  })

  const [createTemplateApi, isCreating] = useApiCall<
    CreateReplyTemplatePayload,
    CreateReplyTemplateResponse
  >(createReplyTemplate)
  const [updateTemplateApi, isUpdating] = useApiCall<
    UpdateReplyTemplatePayload,
    UpdateReplyTemplateResponse
  >(updateReplyTemplate)
  const [deleteTemplateApi, isDeleting] = useApiCall<number, { deletedId: number }>(
    deleteReplyTemplate,
  )

  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [formValues, setFormValues] = useState<TemplateFormValues>(emptyForm)
  const [templateToEdit, setTemplateToEdit] = useState<ReviewReplyTemplate | null>(null)

  const isSaving = isCreating || isUpdating
  const isSubmitDisabled = isSaving || !formValues.title.trim() || !formValues.body.trim()

  const closeDialog = () => {
    setIsDialogOpen(false)
    setTemplateToEdit(null)
    setFormValues(emptyForm)
  }

  const handleCreate = () => {
    setTemplateToEdit(null)
    setFormValues(emptyForm)
    setIsDialogOpen(true)
  }

  const handleEdit = (template: ReviewReplyTemplate) => {
    setTemplateToEdit(template)
    setFormValues({
      title: template.title,
      body: template.body,
      isActive: template.isActive,
    })
    setIsDialogOpen(true)
  }

  const handleDelete = async (template: ReviewReplyTemplate) => {
    try {
      await deleteTemplateApi(template.id)
      setTemplates(templates.filter(item => item.id !== template.id))
      snackbar.show('Plantilla eliminada')
    } catch (error) {
      snackbar.show((error as Error)?.message || ERRORS.GENERIC_ERROR_MESSAGE)
    }
  }

  const handleSave = async () => {
    try {
      if (templateToEdit) {
        const { replyTemplate } = await updateTemplateApi({
          id: templateToEdit.id,
          title: formValues.title.trim(),
          body: formValues.body.trim(),
          isActive: formValues.isActive,
          isAutomatic: true,
        })
        setTemplates(templates.map(item => (item.id === replyTemplate.id ? replyTemplate : item)))
        snackbar.show('Plantilla actualizada')
      } else {
        const { replyTemplate } = await createTemplateApi({
          title: formValues.title.trim(),
          body: formValues.body.trim(),
          isAutomatic: true,
          isActive: formValues.isActive,
        })
        setTemplates([...templates, replyTemplate])
        snackbar.show('Plantilla creada')
      }
      closeDialog()
    } catch (error) {
      snackbar.show((error as Error)?.message || ERRORS.GENERIC_ERROR_MESSAGE)
    }
  }

  const actions = [
    {
      icon: 'add',
      tooltip: 'Nueva plantilla automática',
      isFreeAction: true,
      onClick: handleCreate,
    },
    () => ({
      icon: 'edit',
      tooltip: 'Editar',
      onClick: (_: React.SyntheticEvent, rowData: ReviewReplyTemplate | ReviewReplyTemplate[]) =>
        handleEdit(rowData as ReviewReplyTemplate),
    }),
    () => ({
      icon: 'delete',
      tooltip: 'Eliminar',
      disabled: isDeleting,
      onClick: (_: React.SyntheticEvent, rowData: ReviewReplyTemplate | ReviewReplyTemplate[]) =>
        handleDelete(rowData as ReviewReplyTemplate),
    }),
  ]

  return (
    <>
      <DefaultTable
        title="Respuestas Automáticas para Reseñas Positivas"
        columns={columns}
        data={templates}
        isLoading={isLoading}
        actions={actions}
      />
      <Dialog open={isDialogOpen} onClose={closeDialog} fullWidth maxWidth="sm">
        <DialogTitle>
          {templateToEdit ? 'Editar respuesta automática' : 'Nueva respuesta automática'}
        </DialogTitle>
        <DialogContent>
          <CustomTextField
            autoFocus
            margin="dense"
            label="Título de la plantilla"
            fullWidth
            maxLength={100}
            value={formValues.title}
            onChange={event => setFormValues({ ...formValues, title: event.target.value })}
          />
          <TextField
            margin="dense"
            label="Cuerpo de la respuesta"
            fullWidth
            multiline
            rows={5}
            variant="outlined"
            value={formValues.body}
            onChange={event => setFormValues({ ...formValues, body: event.target.value })}
            placeholder="Texto que se publicará automáticamente en Google..."
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={formValues.isActive}
                onChange={event => setFormValues({ ...formValues, isActive: event.target.checked })}
                color="primary"
              />
            }
            label="Activa (participa en la rotación automática)"
            style={{ marginTop: 8 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} color="default">
            Cancelar
          </Button>
          <Button onClick={handleSave} color="primary" disabled={isSubmitDisabled}>
            Guardar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export { AutoReplyTemplatesSettings }
